import React from "react";
import { useTranslation } from "react-i18next";
import axios from "@/common/axios";
import type { Person, PersonAttrs } from "@/stores/definitions/Person";
import type { Fetched } from "@/util/fetchingData";
import useOauthToken from "../auth/useOauthToken";

/**
 * Get the current user's information from the /api/v1/userDetails/whoami endpoint.
 */
export default function useWhoAmI(): Fetched<Person> {
  const { getToken } = useOauthToken();
  const { t } = useTranslation("common");
  const [currentUser, setCurrentUser] = React.useState<Fetched<Person>>({
    tag: "loading",
  });

  React.useEffect(() => {
    void (async () => {
      try {
        const { data } = await axios.get<PersonAttrs>("/api/v1/userDetails/whoami", {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        });
        setCurrentUser({
          tag: "success",
          value: {
            id: data.id,
            username: data.username,
            firstName: data.firstName,
            lastName: data.lastName,
            hasPiRole: data.hasPiRole,
            hasSysAdminRole: data.hasSysAdminRole,
            email: data.email,
            bench: null,
            workbenchId: data.workbenchId,
            getBench: () => Promise.reject(new Error("Not implemented by this Person implementation")),
            isCurrentUser: true,
            fullName: `${data.firstName} ${data.lastName}`,
            label: `${data.firstName} ${data.lastName} (${data.username})`,
          },
        });
      } catch (error) {
        setCurrentUser({
          tag: "error",
          error: error instanceof Error ? error.message : t("apiErrors.unknown"),
        });
      }
    })();
  }, [getToken, t]);

  return currentUser;
}
